import React from "react";
import { Button, Grid, Typography } from "@material-ui/core";
import { Formik, Form } from "formik";
import * as yup from "yup";
import CustomTextField from "../../forms/CustomTextField";
import { CertificateVerification } from "./CertificateVerification";
import { CertificateVerificationInputData } from "../../../models/CertificateQRCodeVerificationData";
import { useTranslation } from "react-i18next";
import { useDynamicYupValidations } from "../../../libs/yup-validations";

interface Props {
  // eslint-disable-next-line no-unused-vars
  onSubmit: (values: CertificateVerificationInputData) => void;
}

/**
 * Form for entering certificate verification data.
 *
 * @param onSubmit - Called with form values on submit
 */
const CertificateVerificationInputForm: React.FC<Props> = ({ onSubmit }) => {
  const { t } = useTranslation(["certificate", "snackbars"]);

  const { YupValidationCertificateVerification } = useDynamicYupValidations();

  const validationSchema = yup.object({
    year_of_birth: YupValidationCertificateVerification(4),
  });

  const initialValues: CertificateVerificationInputData = {
    last_name_letters: "",
    year_of_birth: "",
  };

  const handleCleanedChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    // eslint-disable-next-line no-shadow,no-unused-vars
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  ) => {
    if (e.target.name === "last_name_letters") {
      e.target.value = e.target.value.replace(/[0-9\s'\-_]/g, "");
    } else if (e.target.name === "year_of_birth") {
      e.target.value = e.target.value.replace(/\D/g, "");
    }

    handleChange(e);
  };

  return (
    <CertificateVerification
      emoticon=""
      heading={t("verification.input.title", { ns: "certificate" })}
      text={t("verification.input.description", { ns: "certificate" })}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={(values) => {
          onSubmit(values);
        }}
      >
        {({ handleChange, isValid }) => (
          <Form>
            <Grid container direction="column" spacing={3}>
              <Grid item xs={6} sm={6}>
                <Typography style={{ marginBottom: 10 }}>
                  {t("verification.input.yearOfBirth", { ns: "certificate" })}
                </Typography>
                <CustomTextField
                  required
                  id="yearOfBirth"
                  name="year_of_birth"
                  placeholder="_ _ _ _"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleCleanedChange(e, handleChange)
                  }
                  inputProps={{
                    maxLength: 4,
                    inputMode: "decimal",
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={6}>
                <Typography style={{ marginBottom: 10 }}>
                  {t("verification.input.lastNameLetters", { ns: "certificate" })}
                </Typography>
                <CustomTextField
                  required
                  id="lastNameLetters"
                  name="last_name_letters"
                  placeholder="_ _ _"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleCleanedChange(e, handleChange)
                  }
                  inputProps={{
                    maxLength: 3,
                    inputMode: "text",
                    style: {
                      textTransform: "lowercase",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!isValid}
                >
                  {t("verification.input.confirm", { ns: "certificate" })}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </CertificateVerification>
  );
};

export default CertificateVerificationInputForm;
